import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import Icon from '../../common/Icons';
import { formatNumber } from '../ui';
import { stockStatusLabel } from '../pharmacyCopy';
import { suggestOrderQty } from './SmartOrderComposer';
import { searchMedications } from './medicationSearch';
import { HighlightedText, useSmartPickerControls } from './smartPickerControls';

function stockPillClass(status) {
  if (status === 'OUT_OF_STOCK') return 'cp-status-pill--danger';
  if (status === 'LOW_STOCK') return 'cp-status-pill--warn';
  return 'cp-status-pill--ok';
}

function lineKey(line, idx) {
  return line.medication_id ? `m-${line.medication_id}` : `i-${idx}`;
}

/**
 * Création intelligente d’une demande d’appro (stock du site demandeur).
 */
export default function SmartSupplyRequestComposer({
  medications = [],
  siteId,
  siteName = '',
  value,
  onChange,
  copy
}) {
  const [stockByMed, setStockByMed] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef(null);
  const listRef = useRef(null);

  const selectedIds = useMemo(
    () => new Set(value.items.map((l) => String(l.medication_id)).filter(Boolean)),
    [value.items]
  );

  const medById = useMemo(() => {
    const map = {};
    medications.forEach((m) => {
      map[String(m.id)] = m;
    });
    return map;
  }, [medications]);

  useEffect(() => {
    if (!siteId) {
      setStockByMed({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setStockLoading(true);
      try {
        const res = await pharmacyService.getSiteStock(siteId);
        if (cancelled) return;
        const map = {};
        (res.success ? res.data || [] : []).forEach((row) => {
          map[String(row.medication_id)] = row;
        });
        setStockByMed(map);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const patch = useCallback(
    (key, next) => onChange({ ...value, [key]: next }),
    [onChange, value]
  );

  const patchItems = useCallback(
    (items) => onChange({ ...value, items }),
    [onChange, value]
  );

  const buildLineFromMed = useCallback(
    (med) => {
      const stock = stockByMed[String(med.id)];
      return {
        medication_id: String(med.id),
        quantity_requested: String(suggestOrderQty(stock))
      };
    },
    [stockByMed]
  );

  const addMedication = useCallback(
    (med) => {
      if (selectedIds.has(String(med.id))) return false;
      const line = buildLineFromMed(med);
      const kept = value.items.filter((l) => l.medication_id);
      patchItems([line, ...kept]);
      setPickerQuery('');
      return true;
    },
    [selectedIds, buildLineFromMed, value.items, patchItems]
  );

  const addMedicationRef = useRef(addMedication);
  addMedicationRef.current = addMedication;

  const pickerResults = useMemo(() => {
    const candidates = medications.filter((m) => !selectedIds.has(String(m.id)));
    return searchMedications(candidates, pickerQuery, { limit: 12 }).map((m) => ({
      med: m,
      stock: stockByMed[String(m.id)]
    }));
  }, [medications, pickerQuery, selectedIds, stockByMed]);

  const pickerResultsRef = useRef(pickerResults);
  pickerResultsRef.current = pickerResults;

  const { open, setOpen, active, setActive, onKeyDown } = useSmartPickerControls({
    itemCount: pickerResults.length,
    onPickIndex: (idx) => {
      const row = pickerResultsRef.current[idx];
      if (row) addMedicationRef.current(row.med);
    }
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [setOpen]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pickMedication = (med) => {
    if (addMedication(med)) setOpen(false);
  };

  const updateLine = (idx, patchLine) => {
    patchItems(value.items.map((line, i) => (i === idx ? { ...line, ...patchLine } : line)));
  };

  const removeLine = (idx) => {
    patchItems(value.items.filter((_, i) => i !== idx));
  };

  const lowStockSuggestions = useMemo(() => {
    return Object.values(stockByMed)
      .filter((s) => s.stock_status === 'LOW_STOCK' || s.stock_status === 'OUT_OF_STOCK')
      .filter((s) => !selectedIds.has(String(s.medication_id)))
      .sort((a, b) => Number(a.on_hand) - Number(b.on_hand));
  }, [stockByMed, selectedIds]);

  const fillFromNeeds = () => {
    if (!lowStockSuggestions.length) return;
    const toAdd = lowStockSuggestions.slice(0, 40).map((s) => {
      const med = medById[String(s.medication_id)] || {
        id: s.medication_id,
        name: s.medication_name,
        unit_name: s.unit_name
      };
      return buildLineFromMed(med);
    });
    const kept = value.items.filter((l) => l.medication_id);
    patchItems([...toAdd, ...kept]);
  };

  const validCount = value.items.filter((l) => l.medication_id && Number(l.quantity_requested) > 0).length;

  return (
    <div className="cp-smart-order">
      <section className="cp-form-section">
        <h3 className="cp-form-section__title">{copy.SECTION_HEADER}</h3>
        <div className="cp-form-grid">
          <Form.Group className="cp-form-field">
            <Form.Label>{copy.FIELD_SITE}</Form.Label>
            <Form.Control type="text" value={siteName || '—'} disabled readOnly />
            <Form.Text>{copy.FIELD_SITE_HINT}</Form.Text>
          </Form.Group>
          <Form.Group className="cp-form-field">
            <Form.Label>{copy.FIELD_PRIORITY}</Form.Label>
            <Form.Select value={value.priority} onChange={(e) => patch('priority', e.target.value)}>
              <option value="NORMAL">{copy.PRIORITY_NORMAL}</option>
              <option value="URGENT">{copy.PRIORITY_URGENT}</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="cp-form-field cp-form-field--full">
            <Form.Label>{copy.FIELD_NOTES}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={value.notes}
              onChange={(e) => patch('notes', e.target.value)}
              placeholder={copy.FIELD_NOTES_PLACEHOLDER}
            />
          </Form.Group>
        </div>
      </section>

      <section className="cp-form-section cp-smart-order__lines">
        <div className="cp-form-section__head">
          <h3 className="cp-form-section__title mb-0">{copy.SECTION_LINES}</h3>
          <span className="cp-form-section__meta">
            {copy.LINES_COUNT.replace('{n}', String(validCount))}
          </span>
        </div>

        <div className="cp-smart-order__assist">
          <div className="cp-smart-order__assist-text">
            <Icon name="bell" size={15} aria-hidden />
            <span>
              {!siteId
                ? copy.SMART_NO_SITE
                : stockLoading
                  ? copy.SMART_STOCK_LOADING
                  : copy.SMART_STOCK_HINT.replace('{n}', String(lowStockSuggestions.length))}
            </span>
          </div>
          <button
            type="button"
            className="cp-btn cp-btn--ghost cp-btn--sm"
            disabled={!lowStockSuggestions.length}
            onClick={fillFromNeeds}
          >
            <Icon name="plus" size={14} aria-hidden />
            {copy.SMART_FILL_NEEDS}
            {lowStockSuggestions.length ? ` (${lowStockSuggestions.length})` : ''}
          </button>
        </div>

        <div className="cp-smart-picker" ref={pickerRef}>
          <label className="cp-form-field mb-0 w-100">
            <span className="form-label">{copy.SMART_ADD_LABEL}</span>
            <div className="cp-smart-picker__control">
              <Icon name="search" size={16} className="cp-smart-picker__icon" aria-hidden />
              <input
                type="search"
                className="cp-smart-picker__input"
                placeholder={copy.SMART_ADD_PLACEHOLDER}
                value={pickerQuery}
                onChange={(e) => {
                  setPickerQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-controls="cp-supply-med-list"
                aria-autocomplete="list"
              />
            </div>
          </label>
          {open ? (
            <ul className="cp-smart-picker__list" id="cp-supply-med-list" role="listbox" ref={listRef}>
              {pickerResults.length === 0 ? (
                <li className="cp-smart-picker__empty">{copy.SMART_NO_MATCH}</li>
              ) : (
                pickerResults.map(({ med, stock }, idx) => {
                  const isActive = idx === active;
                  return (
                    <li key={med.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive ? 'true' : undefined}
                        className={`cp-smart-picker__option${isActive ? ' is-active' : ''}`}
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => pickMedication(med)}
                      >
                        <span className="cp-smart-picker__option-main">
                          <strong>
                            <HighlightedText text={med.name} query={pickerQuery} />
                          </strong>
                          <span className="cp-smart-picker__option-meta">
                            {med.unit_name || 'unités'}
                            {stock
                              ? ` · stock ${formatNumber(stock.on_hand)} / min ${formatNumber(stock.min_stock)}`
                              : ''}
                          </span>
                        </span>
                        {stock ? (
                          <span className={`cp-status-pill ${stockPillClass(stock.stock_status)}`}>
                            {stockStatusLabel(stock.stock_status)}
                          </span>
                        ) : (
                          <span className="cp-smart-picker__add">{copy.SMART_ADD_CTA}</span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          ) : null}
        </div>

        {value.items.filter((l) => l.medication_id).length === 0 ? (
          <div className="cp-smart-order__empty">
            <Icon name="pills" size={22} aria-hidden />
            <p className="mb-0">{copy.SMART_EMPTY_LINES}</p>
          </div>
        ) : (
          <div className="cp-table-wrap cp-smart-lines-table-wrap">
            <table className="cp-table cp-smart-lines-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{copy.COL_MED}</th>
                  <th>{copy.COL_UNIT}</th>
                  <th className="text-end">{copy.COL_SITE_STOCK}</th>
                  <th className="text-end">{copy.COL_MIN}</th>
                  <th>{copy.COL_STATUS}</th>
                  <th className="text-end" style={{ minWidth: 110 }}>
                    {copy.SMART_QTY}
                  </th>
                  <th className="text-end" style={{ width: 52 }} />
                </tr>
              </thead>
              <tbody>
                {value.items
                  .map((line, idx) => ({ line, idx }))
                  .filter(({ line }) => line.medication_id)
                  .map(({ line, idx }, displayIdx, rows) => {
                    const med = medById[String(line.medication_id)];
                    const stock = stockByMed[String(line.medication_id)];
                    const name =
                      med?.name || stock?.medication_name || `Médicament #${line.medication_id}`;
                    const unit = med?.unit_name || stock?.unit_name || '—';
                    const rowNum = rows.length - displayIdx;

                    return (
                      <tr key={lineKey(line, idx)}>
                        <td className="cp-num text-muted">{rowNum}</td>
                        <td>
                          <strong className="cp-smart-lines-table__name">{name}</strong>
                        </td>
                        <td>
                          <span className="text-muted small">{unit}</span>
                        </td>
                        <td className="text-end cp-num">
                          {stock ? formatNumber(stock.on_hand) : '—'}
                        </td>
                        <td className="text-end cp-num">
                          {stock ? formatNumber(stock.min_stock) : '—'}
                        </td>
                        <td>
                          {stock ? (
                            <span className={`cp-status-pill ${stockPillClass(stock.stock_status)}`}>
                              {stockStatusLabel(stock.stock_status)}
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td className="text-end">
                          <Form.Control
                            size="sm"
                            type="number"
                            min={1}
                            className="cp-smart-lines-table__qty"
                            value={line.quantity_requested}
                            onChange={(e) => updateLine(idx, { quantity_requested: e.target.value })}
                            aria-label={`${copy.SMART_QTY} ${name}`}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="cp-icon-btn"
                            onClick={() => removeLine(idx)}
                            aria-label={copy.SMART_REMOVE}
                          >
                            <Icon name="trash" size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
